import { useQuery } from "@tanstack/react-query";
import {
  CanAccess,
  useDataProvider,
  useTranslate,
  type Identifier,
} from "ra-core";
import { Link, Navigate } from "react-router";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import {
  ListCardsSkeleton,
  ListEmptyState,
  ListErrorState,
} from "../design/ListStates";
import { StatusPill } from "../design/StatusPill";
import type { CrmDataProvider } from "../providers/types";
import {
  QUOTER_CONFIG_RESOURCES,
  type AuditLogEntry,
  type QuoterPanel as QuoterPanelData,
  type Sale,
} from "../types";
import {
  QUOTER_ADMIN_QUERY_KEY,
  QuoterProjectPicker,
  useSelectedQuoterProject,
} from "./quoterAdminData";
import { formatMxn, formatWhen } from "./quoterFormat";
import { QuoterApproversAdmin } from "./QuoterApproversAdmin";

const AUDIT_ROWS_PER_TABLE = 20;
const AUDIT_ROWS_SHOWN = 30;

/** Audit entries of the quoter config tables, newest first, with who made them. */
const useConfigAudit = () => {
  const dataProvider = useDataProvider<CrmDataProvider>();
  return useQuery({
    queryKey: [QUOTER_ADMIN_QUERY_KEY, "audit"],
    queryFn: async () => {
      const perTable = await Promise.all(
        QUOTER_CONFIG_RESOURCES.map((table) =>
          dataProvider.getList<AuditLogEntry>("audit_log", {
            pagination: { page: 1, perPage: AUDIT_ROWS_PER_TABLE },
            sort: { field: "occurred_at", order: "DESC" },
            filter: { table_name: table },
          }),
        ),
      );
      const entries = perTable
        .flatMap((result) => result.data)
        .sort((a, b) => b.occurred_at.localeCompare(a.occurred_at))
        .slice(0, AUDIT_ROWS_SHOWN);
      const actorIds = [
        ...new Set(
          entries.flatMap((entry) =>
            entry.actor_sales_id == null ? [] : [entry.actor_sales_id],
          ),
        ),
      ];
      const actors =
        actorIds.length > 0
          ? (await dataProvider.getMany<Sale>("sales", { ids: actorIds })).data
          : [];
      return { entries, actors };
    },
  });
};

const Stat = ({
  label,
  value,
  testId,
}: {
  label: string;
  value: string;
  testId?: string;
}) => (
  <Card>
    <CardContent className="space-y-1 p-4">
      <p className="text-base text-muted-foreground sm:text-sm">{label}</p>
      <p className="text-2xl font-semibold tabular-nums" data-testid={testId}>
        {value}
      </p>
    </CardContent>
  </Card>
);

const PanelBody = ({ projectId }: { projectId: Identifier }) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const panel = useQuery({
    queryKey: [QUOTER_ADMIN_QUERY_KEY, "panel", projectId],
    queryFn: (): Promise<QuoterPanelData | null> =>
      dataProvider.quoterPanel(projectId),
  });

  if (panel.isError) {
    return (
      <ListErrorState
        message={translate("crm.quoter.panel.load_error")}
        onRetry={() => panel.refetch()}
      />
    );
  }
  if (panel.isPending) return <ListCardsSkeleton rows={3} />;
  if (!panel.data) {
    return (
      <ListEmptyState
        title={translate("crm.quoter.panel.no_config")}
        description={translate("crm.quoter.panel.no_config_hint")}
      />
    );
  }
  const data = panel.data;
  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-3">
        <Stat
          label={translate("crm.quoter.panel.current_list")}
          value={data.current_list == null ? "-" : String(data.current_list)}
          testId="panel-current-list"
        />
        <Stat
          label={
            data.next_list == null
              ? translate("crm.quoter.panel.missing_last")
              : translate("crm.quoter.panel.missing_for_list", {
                  list: data.next_list,
                })
          }
          value={
            data.sales_missing_for_next_list == null
              ? "-"
              : String(data.sales_missing_for_next_list)
          }
          testId="panel-sales-missing"
        />
        <Stat
          label={translate("crm.quoter.panel.available_units")}
          value={String(data.available_units)}
          testId="panel-available-units"
        />
      </div>
      <p className="px-1 text-base text-muted-foreground tabular-nums sm:text-sm">
        {translate(
          data.apartados_count_toward_list
            ? "crm.quoter.panel.counted_with_apartados"
            : "crm.quoter.panel.counted_sold_only",
          { count: data.counted_sales },
        )}
      </p>

      <Card>
        <CardContent className="space-y-2 p-4">
          <h2 className="text-lg font-semibold">
            {translate("crm.quoter.panel.scarcity")}
          </h2>
          {data.scarcity.length === 0 ? (
            <p className="text-base text-muted-foreground sm:text-sm">
              {translate("crm.quoter.panel.scarcity_empty")}
            </p>
          ) : (
            <ul className="space-y-2" data-testid="panel-scarcity">
              {data.scarcity.map((row) => (
                <li
                  key={row.stage ?? "none"}
                  className="flex flex-wrap items-center justify-between gap-2 text-base tabular-nums sm:text-sm"
                >
                  <span>
                    {row.stage != null
                      ? translate("crm.quoter.verify.stage", {
                          stage: row.stage,
                        })
                      : translate("crm.quoter.verify.no_stage")}
                    {": "}
                    {translate("crm.quoter.panel.available_count", {
                      count: row.available,
                    })}
                  </span>
                  <StatusPill
                    tone={row.released ? "ok" : "not-ok"}
                    text={translate(
                      row.released
                        ? "crm.quoter.panel.released"
                        : "crm.quoter.panel.not_released",
                    )}
                  />
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {data.models.length > 0 ? (
        <Card>
          <CardContent className="space-y-2 p-4">
            <h2 className="text-lg font-semibold">
              {translate("crm.quoter.panel.models")}
            </h2>
            <ul className="space-y-1 text-base tabular-nums sm:text-sm">
              {data.models.map((model) => (
                <li key={model.model_id} className="flex justify-between gap-3">
                  <span>{model.name}</span>
                  <span>
                    {model.price_from == null
                      ? "-"
                      : translate("crm.quoter.panel.price_from", {
                          price: formatMxn(model.price_from),
                        })}
                  </span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
};

const AuditLog = () => {
  const translate = useTranslate();
  const audit = useConfigAudit();
  const actorName = (entry: AuditLogEntry) => {
    const sale = audit.data?.actors.find(
      (row) => row.id === entry.actor_sales_id,
    );
    return sale
      ? `${sale.first_name} ${sale.last_name}`
      : translate("crm.quoter.panel.audit_unknown_actor");
  };
  return (
    <Card>
      <CardContent className="space-y-2 p-4">
        <h2 className="text-lg font-semibold">
          {translate("crm.quoter.panel.audit_title")}
        </h2>
        {audit.isError ? (
          <ListErrorState
            message={translate("crm.quoter.panel.audit_error")}
            onRetry={() => audit.refetch()}
          />
        ) : audit.isPending ? (
          <ListCardsSkeleton rows={2} />
        ) : audit.data.entries.length === 0 ? (
          <p className="text-base text-muted-foreground sm:text-sm">
            {translate("crm.quoter.panel.audit_empty")}
          </p>
        ) : (
          <ul className="space-y-2" data-testid="panel-audit-log">
            {audit.data.entries.map((entry) => (
              <li key={entry.id} className="text-base sm:text-sm">
                <p className="font-medium">
                  {translate(`crm.quoter.panel.audit_action_${entry.action}`, {
                    table: translate(
                      `crm.quoter.panel.audit_tables.${entry.table_name}`,
                      { _: entry.table_name },
                    ),
                  })}
                </p>
                <p className="text-muted-foreground tabular-nums">
                  {actorName(entry)} · {formatWhen(entry.occurred_at)}
                  {entry.changed_columns?.length
                    ? ` · ${entry.changed_columns.join(", ")}`
                    : ""}
                </p>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};

/**
 * Almikia Epic EQ2 (TASK-099): the Direccion / project panel: the list in
 * force, the sales still missing for the next list, the available units, the
 * scarcity per stage and the audit log of configuration changes (who, when,
 * what). It shows project and stage figures only: there is no ranking between
 * advisors. Gated like the configuration (direccion only).
 */
export const QuoterPanel = () => (
  <CanAccess
    resource="quoter_unit_details"
    action="edit"
    accessDenied={<Navigate to="/" replace />}
  >
    <QuoterPanelContent />
  </CanAccess>
);

QuoterPanel.path = "/quoter_panel";

const QuoterPanelContent = () => {
  const translate = useTranslate();
  const { projects, projectId, setProjectId } = useSelectedQuoterProject();
  return (
    <div className="mx-auto mt-2 max-w-3xl space-y-4 pb-8">
      <div className="flex flex-wrap items-center justify-between gap-2 px-1">
        <h1 className="text-xl font-semibold">
          {translate("crm.quoter.panel.title")}
        </h1>
        <Button asChild variant="outline" className="h-11 sm:h-9">
          <Link to="/quoter_config">{translate("crm.quoter.config.nav")}</Link>
        </Button>
      </div>
      <QuoterApproversAdmin />
      {projects.isError ? (
        <ListErrorState
          message={translate("crm.quoter.panel.load_error")}
          onRetry={() => projects.refetch()}
        />
      ) : projects.isPending ? (
        <ListCardsSkeleton rows={3} />
      ) : (projects.data ?? []).length === 0 ? (
        <ListEmptyState
          title={translate("crm.quoter.config.no_projects")}
          description={translate("crm.quoter.config.no_projects_hint")}
        />
      ) : (
        <>
          <QuoterProjectPicker
            projects={projects.data ?? []}
            value={projectId}
            onChange={setProjectId}
          />
          {projectId == null ? null : <PanelBody projectId={projectId} />}
          <AuditLog />
        </>
      )}
    </div>
  );
};
